import React from 'react'
import { ListItem, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useLocalizeNumberValue } from '@e3dc-react/shell/Hooks/useLocalizeNumberValue'
import { formatDuration } from '@e3dc-react/shell/Datetime'
import { ChargingSessionModel } from '../models/charging-sessions.model'
import { getCurrencyValue } from '../../shared/utils/currency-value'

const useStyles = makeStyles()(theme => ({
  listItem: {
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: 0,
  },
  listItemTitle: {
    overflowWrap: 'anywhere',
    hyphens: 'auto',
    width: '33%',
  },
  listItemValue: {
    width: '67%',
    paddingLeft: theme.spacing(1),
    overflowWrap: 'anywhere',
    hyphens: 'auto',
  },
}))

interface EdisonConnectPricingProps {
  session: ChargingSessionModel
}

/**
 * EdisonConnectPricing component for displaying discharge values only for EdisonConnect wallbox type
 * @param session single session as ChargingSessionResponseContentModel
 * @returns JSX component
 */
export const EdisonConnectPricing: React.VFC<EdisonConnectPricingProps> = ({ session }) => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()
  const localizeNumberValue = useLocalizeNumberValue()

  return (
    <>
      <ListItem divider className={classes.listItem} disableGutters>
        <Typography variant="subtitle1" className={classes.listItemTitle}>
          {t('emobility.chargingSession.discharged-energy')}
        </Typography>

        <Typography variant="body2" className={classes.listItemValue}>
          {t('emobility.chargingSession.total-discharged-energy-value', {
            value: t('common.format.kwh', { value: localizeNumberValue(session.energyDischargedKWh) }),
          })}
        </Typography>
      </ListItem>

      <ListItem divider className={classes.listItem} disableGutters>
        <Typography variant="subtitle1" className={classes.listItemTitle}>
          {t('emobility.chargingSession.discharging-duration')}
        </Typography>
        <Typography variant="body2" className={classes.listItemValue}>
          {formatDuration(session.dischargeTimeActiveMilliseconds)}
        </Typography>
      </ListItem>

      {session.isCostFinal ? (
        <ListItem divider className={classes.listItem} disableGutters>
          <Typography variant="subtitle1" className={classes.listItemTitle}>
            {t('emobility.chargingSession.discharge-price-per-kwh')}
          </Typography>

          <Typography variant="body2" className={classes.listItemValue}>
            {getCurrencyValue(session.dischargePrice, session.currencyCode)}
          </Typography>
        </ListItem>
      ) : null}
    </>
  )
}
